import React, { useMemo } from "react";
import { useDropzone } from "react-dropzone";
import theme from "../../theme";
import { Box } from "@mui/material";
import { useDispatch } from "../../store";
import { CloudUpload } from "@mui/icons-material";
import { Base } from ".";
import { handleToastState } from "../../slices/shared";

export interface Props {
  onDrop: (acceptedFiles: File[]) => void;
  disabled?: boolean;
}

const baseStyle = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: "20px",
  borderWidth: 3,
  borderRadius: "15px",
  borderColor: theme.palette.info.main,
  borderStyle: "dashed",
  backgroundColor: theme.palette.background.paper,
  color: theme.palette.primary.main,
  outline: "none",
  transition: "border .24s ease-in-out",
  cursor: "pointer",
};

const activeStyle = {
  borderColor: theme.palette.primary.main,
};

const acceptStyle = {
  borderColor: theme.palette.success.main,
};

const rejectStyle = {
  borderColor: theme.palette.error.main,
};

const Dropzone: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    accept: {
      "image/*": [".png", ".jpg", ".jpeg", ".xlsx", ".pdf", ".docx", ".doc"],
      "video/*": [".mov", ".mpv", ".mp4", ".wmv", ".wav"],
      "audio/*": [
        ".aac",
        ".mid",
        ".midi",
        ".mp3",
        ".oga",
        ".opus",
        ".wav",
        ".weba",
        ".3gp",
        ".3g2",
      ],
      "application/vnd.ms-outlook": [".msg"],
      "message/rfc822": [".eml"],
    },
    maxSize: 524288000,
    disabled: !!props.disabled,
    onDrop: async (acceptedFiles, fileRejections) => {
      fileRejections.forEach((file) => {
        file.errors.forEach((err) => {
          if (err.code === "file-too-large") {
            dispatch(
              handleToastState({
                open: "error",
                message:
                  "Error: File is too large! Please resize and try again.",
              })
            );
          }
          if (err.code === "file-invalid-type") {
            dispatch(
              handleToastState({
                open: "error",
                message:
                  "Error: File type not accepted! Please convert to another file type and try again.",
              })
            );
          }
        });
      });
      await props.onDrop(acceptedFiles);
    },
  });

  const style = useMemo(
    () => ({
      ...baseStyle,
      backgroundColor: !!props.disabled ? "#d4d4d4" : baseStyle.backgroundColor,
      color: !!props.disabled ? "#3a3b3c" : baseStyle.color,
      cursor: !!props.disabled ? "not-allowed" : baseStyle.cursor,
      ...(isDragActive ? activeStyle : {}),
      ...(isDragAccept ? acceptStyle : {}),
      ...(isDragReject ? rejectStyle : {}),
    }),
    [isDragActive, isDragReject, isDragAccept, props.disabled]
  );

  return (
    <>
      <Box
        className="container"
        display="flex"
        flexDirection="row"
        sx={{ marginBottom: 2 }}
      >
        <Box {...getRootProps({ style })} display="flex" flexDirection="column">
          <input {...getInputProps()} />
          <Box display="flex" flexDirection="row" sx={{ marginBottom: 1 }}>
            <CloudUpload />
          </Box>
          <Base.Text
            text="Drag and drop to upload a file here, or click to select"
            center
          />
        </Box>
      </Box>
    </>
  );
};

export default Dropzone;
